// Chart and insight primitives shared by the dashboard's analysis modals.
//
// These were local to components/pages/Dashboard.js until the Feed Analysis
// modal needed the same corridor chart and the same insight rail. They live
// here rather than being exported from Dashboard.js because Dashboard.js
// imports FeedAnalysisModal, and importing back would make a module cycle.
//
// Moved verbatim -- if any of this behaves differently from the FCR and Weight
// modals did before the move, that is a bug in the move, not an improvement.

import React from 'react';
import ReactECharts from 'echarts-for-react';
import { useTranslation } from 'react-i18next';
import MaterialIcon from 'material-icons-react';

export function statusColor(status) {
  switch (status) {
    case 'Ahead of Target': return '#22C55E';
    case 'On Track': return '#3B82F6';
    case 'Behind Target': return '#F59E0B';
    default: return '#9CA3AF';
  }
}

// Shared renderer for the insight rail.
export function InsightCards({ cards }) {
  if (!cards || !cards.length) return null;
  return (
    <div className="forecastInsights">
      {cards.map((c) => (
        <div key={c.key} className={`insightCard insight-${c.tone}`}>
          <div className="insightIcon"><MaterialIcon icon={c.icon} size={18} /></div>
          <div className="insightText">
            <div className="insightTitle">{c.title}</div>
            <div className="insightBody">{c.body}</div>
          </div>
        </div>
      ))}
    </div>
  );
}

// Hex -> rgba, for the gradient fill under the "actual so far" segment.
export function hexToRgba(hex, alpha) {
  const h = String(hex || '#292970').replace('#', '');
  const n = h.length === 3 ? h.split('').map((c) => c + c).join('') : h;
  const num = parseInt(n, 16);
  if (!Number.isFinite(num)) return `rgba(41,41,112,${alpha})`;
  return `rgba(${(num >> 16) & 255},${(num >> 8) & 255},${num & 255},${alpha})`;
}

// Forecast chart — target corridor.
//
// Earlier versions drew the flock and the ideal as two cumulative curves. Both
// climb monotonically toward ~2.0, so most of the ink restated "FCR rises with
// age" — true of every flock ever — while the entire management story (ahead or
// behind, by how much) was a sliver of gap between two dashed lines. This draws
// the comparison the way breeder management guides do: the ideal becomes a
// shaded ON-TRACK ZONE (ideal ± bandPct), and the flock is the ONLY line —
// bold, solid where recorded, dashed where projected.
//
// bandPct defaults to 5% because statusFromRatio() flips the status chip at
// exactly 0.95/1.05. A line below the zone (for lowerIsBetter metrics) IS the
// "Ahead of Target" condition made visible — chart and chip cannot disagree.
//
// The zone is built with ECharts' stacked-line idiom: an invisible line at the
// band's lower edge plus a stacked, area-filled line carrying the band HEIGHT.
export function ProjectionMiniChart({
  days, predicted, ideal, today, todayLabel = null, color,
  yInterval, unitSuffix = '', decimals = 2,
  lowerIsBetter = false, height = 300, splitAt = null, trimStart = 0,
  bandPct = 0.05,
  // Explicit corridor edges, for a band that is not a symmetric percentage of
  // the ideal curve. Feed Analysis passes "the standard, and the standard N
  // days further on", which is wide early and tight late -- a +-% band cannot
  // express that, and would put every healthy flock far outside itself for the
  // first three weeks. When either is omitted the +-bandPct behaviour stands.
  bandLo: bandLoProp = null, bandHi: bandHiProp = null,
  // Overrides for a band that is not a "target". Feed's corridor is a normal
  // operating range, not something to hit.
  zoneLabel = null, zoneLabelColon = null,
  // 'higher' | 'lower' | 'neutral'. Feed passes 'neutral': more feed delivered
  // than standard is neither good nor bad, and colouring it green or red would
  // assert exactly the judgement this module refuses to make.
  deltaTone = null,
}) {
  // named `tr` here: this component already has a numeric `const t = Number(today)`
  const { t: tr } = useTranslation(['dashboard', 'common']);
  // default resolved here, not in the parameter list: the hook is not in scope there
  const todayLabelText = todayLabel ?? tr('dashboard:labels.today');
  // Series names are compared against in the tooltip formatter below, so they
  // must come from one place — comparing against an English literal breaks
  // as soon as the series is renamed by translation.
  const NAME_ZONE = zoneLabel || tr('dashboard:labels.targetZone');
  const NAME_SOFAR = tr('dashboard:labels.soFar');
  const NAME_FORECAST = tr('dashboard:labels.forecast');
  const t = Number(today);
  const fmt = (v) => (v == null || isNaN(Number(v)) ? '—' : Number(v).toFixed(decimals) + unitSuffix);

  const rawN = Array.isArray(predicted) ? predicted.length : 0;

  // Drop the first few days from the FCR view.
  //
  // Cumulative FCR in the first week is a near-vertical ramp off ~0 purely
  // because the birds have barely eaten. Plotted, it forces the y-axis down and
  // squashes the 0.9-2.0 band — where every decision lives — into the top of
  // the panel. Cut from the CHART only; the API response and the stat cards are
  // untouched. Skipped when the flock has not yet passed the trim point.
  const off = (trimStart > 0 && rawN > trimStart + 3
               && Number.isFinite(Number(splitAt)) && Number(splitAt) > trimStart)
    ? trimStart : 0;
  if (off > 0) {
    days = days.slice(off);
    predicted = predicted.slice(off);
    ideal = Array.isArray(ideal) ? ideal.slice(off) : ideal;
    bandLoProp = Array.isArray(bandLoProp) ? bandLoProp.slice(off) : bandLoProp;
    bandHiProp = Array.isArray(bandHiProp) ? bandHiProp.slice(off) : bandHiProp;
  }
  const n = Array.isArray(predicted) ? predicted.length : 0;

  // Where the record stops and the estimate starts. Clamped into range so a
  // fallback measurement day far outside the series cannot blank the chart.
  const cut = (splitAt == null || !Number.isFinite(Number(splitAt)))
    ? null
    : Math.max(0, Math.min(n - 1, Math.round(Number(splitAt)) - off));

  // Two arrays over the same x-grid. The projected half repeats the point AT the
  // cut so the two segments meet with no visual gap.
  const soFar = cut == null ? [] : predicted.map((v, i) => (i <= cut ? v : null));
  const projected = cut == null ? predicted : predicted.map((v, i) => (i >= cut ? v : null));

  // Does a forecast segment actually EXIST? `projected` repeats the point at
  // the cut, so one non-null point is a dot, not a line. Feed Analysis passes a
  // series that simply stops at the last delivery -- deliveries are recorded,
  // not predicted -- so without this the legend would advertise a "Forecast"
  // that is never drawn.
  const projectedPts = projected.filter((v) => v != null).length;
  const hasForecast = cut == null ? projectedPts > 0 : projectedPts >= 2;

  // Last non-null projected point — gets a labelled end marker.
  let lastIdx = -1;
  for (let i = n - 1; i >= 0; i--) { if (projected[i] != null) { lastIdx = i; break; } }

  // Both markers ride on the Forecast series as markPoints. (A single-point
  // helper series of nulls does NOT render in ECharts 5; markPoint is the
  // supported mechanism and is not clipped at the plot's right edge.)
  const markPointData = [];
  if (lastIdx >= 0 && predicted[lastIdx] != null) {
    markPointData.push({
      coord: [String(days[lastIdx]), predicted[lastIdx]],
      symbol: 'circle', symbolSize: 11,
      itemStyle: { color, borderColor: '#fff', borderWidth: 2 },
      label: {
        show: true, position: 'top', distance: 8,
        formatter: fmt(predicted[lastIdx]),
        color: '#fff', fontSize: 13, fontWeight: 800,
        backgroundColor: color, padding: [4, 8], borderRadius: 5,
      },
    });
  }
  if (cut != null && predicted[cut] != null) {
    markPointData.push({
      coord: [String(days[cut]), predicted[cut]],
      symbol: 'circle', symbolSize: 10,
      itemStyle: { color: '#fff', borderColor: color, borderWidth: 3 },
      label: { show: false },
    });
  }

  // The corridor: lower edge and band height, from the ideal curve.
  const idealArr = Array.isArray(ideal) ? ideal : [];
  const explicitBand = Array.isArray(bandLoProp) && Array.isArray(bandHiProp);
  const bandLo = explicitBand
    ? bandLoProp.map((v) => (v == null ? null : +Number(v).toFixed(4)))
    : idealArr.map((v) => (v == null ? null : +(v * (1 - bandPct)).toFixed(4)));
  // The zone series carries the band HEIGHT, stacked on the lower edge.
  const bandH = explicitBand
    ? bandHiProp.map((v, i) => {
        const lo = bandLoProp[i];
        return (v == null || lo == null) ? null : +Math.max(0, Number(v) - Number(lo)).toFixed(4);
      })
    : idealArr.map((v) => (v == null ? null : +(v * 2 * bandPct).toFixed(4)));

  // Blue-600. As a background wash this renders about #c0d1f7, which stays
  // separable from every line that can sit on top of it -- the on-track blue,
  // the ahead green, the behind amber and the feed teal (worst normal-vision
  // pair 17.3, above the 15 floor). Compared as a RAW hex it would look like a
  // clash with the on-track line; it is not, because the band is drawn at 0.28
  // alpha and the line is solid. Keep the alphas if you change the hue.
  const ZONE_HUE = '#2563EB';
  const ZONE_FILL = hexToRgba(ZONE_HUE, 0.28);
  const ZONE_EDGE = hexToRgba(ZONE_HUE, 0.40);   // thin crisp boundary lines
  const ZONE_SWATCH = hexToRgba(ZONE_HUE, 0.65); // legend needs a visible chip

  const series = [
    // Carrier for the band's lower edge — drawn thin so the zone has a crisp
    // bottom boundary instead of fading into the background.
    {
      name: '__bandLo', type: 'line', data: bandLo, stack: 'band',
      showSymbol: false, lineStyle: { color: ZONE_EDGE, width: 1 }, silent: true,
      legendHoverLink: false, z: 1,
    },
    // Band height stacked on the carrier; its area IS the zone, its line the
    // zone's top boundary.
    {
      name: NAME_ZONE, type: 'line', data: bandH, stack: 'band',
      showSymbol: false, lineStyle: { color: ZONE_EDGE, width: 1 }, silent: true,
      areaStyle: { color: ZONE_FILL },
      itemStyle: { color: ZONE_SWATCH },
      z: 1,
    },
  ];

  // Recorded so far — the flock's one bold line, solid.
  if (cut != null) {
    series.push({
      name: NAME_SOFAR, type: 'line', data: soFar,
      smooth: true, showSymbol: false,
      lineStyle: { color, width: 4 },
      itemStyle: { color },
      z: 4,
    });
  }

  // Projected — same colour, dashed.
  series.push({
    // The markLine (today) and markPoint (end badge) ride on this series, so it
    // is always created -- but when there is nothing to forecast it takes a
    // `__` name, which keeps it out of the legend.
    name: hasForecast ? NAME_FORECAST : '__noForecast', type: 'line', data: projected,
    smooth: true, showSymbol: false,
    lineStyle: { color, width: 3.4, type: cut == null ? 'solid' : [8, 6] },
    itemStyle: { color },
    z: 3,
    markPoint: markPointData.length
      ? { silent: true, animation: false, data: markPointData }
      : undefined,
    markLine: (t > 0 && days.length && Number(days[0]) <= t) ? {
      symbol: 'none', silent: true,
      label: {
        formatter: todayLabelText, color: '#292970', fontSize: 12, fontWeight: 800,
        position: 'insideStartTop', rotate: 0, padding: [3, 6],
        backgroundColor: 'rgba(255,255,255,0.92)', borderRadius: 4,
      },
      lineStyle: { color: '#9aa0c0', type: 'dashed', width: 1.8 },
      data: [{ xAxis: String(t) }],
    } : undefined,
  });

  // Only real, user-facing series belong in the legend — the carrier is named
  // with a __ prefix precisely so it can be filtered out here.
  // Two dashes, drawn into the legend item box -- the swatch then matches the
  // dashed line on the plot instead of restating the solid one beside it.
  const DASH_ICON = 'path://M0,0 L8,0 L8,4 L0,4 Z M12,0 L20,0 L20,4 L12,4 Z';
  const legendNames = series
    .map((sr) => sr.name)
    .filter((nm) => nm && !nm.startsWith('__'))
    .filter((nm) => hasForecast || nm !== NAME_FORECAST)
    .map((nm) => (nm === NAME_FORECAST ? { name: nm, icon: DASH_ICON } : nm));

  const option = {
    // No `color` palette: every series pins its own itemStyle above, so legend
    // swatches always match their lines however many series are present.
    // Right margin holds the end-of-cycle value badge, which sits at the very
    // last x position and would otherwise be clipped by the plot edge.
    grid: { top: 46, right: 56, bottom: 46, left: 56 },
    legend: {
      top: 4, left: 'center', icon: 'roundRect', itemWidth: 20, itemHeight: 5, itemGap: 20,
      textStyle: { fontSize: 13.5, color: '#292970', fontWeight: 700 },
      data: legendNames,
    },
    tooltip: {
      trigger: 'axis',
      // Without this the tooltip renders outside the modal near the right edge.
      confine: true,
      axisPointer: { type: 'line', lineStyle: { color: '#c5c8df', type: 'dashed' } },
      backgroundColor: 'rgba(41,41,112,0.96)',
      borderWidth: 0,
      textStyle: { color: '#fff', fontSize: 12 },
      padding: [8, 12],
      formatter: (params) => {
        if (!params || !params.length) return '';
        // Read from the closure arrays by index — the stacked band series carry
        // band HEIGHT, not values, so params alone would mislead here.
        const idx = params[0].dataIndex;
        const idealV = idealArr[idx];
        const predV = predicted[idx];
        const loV = explicitBand ? bandLo[idx] : (idealV != null ? idealV * (1 - bandPct) : null);
        const hiV = explicitBand
          ? (bandLo[idx] != null && bandH[idx] != null ? bandLo[idx] + bandH[idx] : null)
          : (idealV != null ? idealV * (1 + bandPct) : null);
        let deltaLine = '';
        if (idealV != null && predV != null) {
          const d = Number(predV) - Number(idealV);
          const tone = deltaTone || (lowerIsBetter ? 'lower' : 'higher');
          const good = tone === 'lower' ? d < 0 : d > 0;
          const col = (tone === 'neutral' || d === 0) ? '#c7c7e6' : (good ? '#86efac' : '#fca5a5');
          deltaLine = `<div style="margin-top:3px;color:${col};font-weight:700;">${d > 0 ? '+' : ''}${d.toFixed(decimals)} ${tr('dashboard:labels.vsTarget')}</div>`;
        }
        const axisDay = params[0].axisValue;
        const isPast = cut != null && Number(axisDay) <= Number(days[cut]);
        const phase = cut == null ? '' :
          `<span style="opacity:.7;font-weight:600;"> · ${isPast ? tr('dashboard:labels.recorded') : tr('dashboard:labels.projectedLower')}</span>`;
        return `<div style="font-weight:700;margin-bottom:3px;">${tr('common:time.day')} ${axisDay}${phase}</div>`
          + `<div>${tr('dashboard:labels.yourFlockWithColon')} <b>${fmt(predV)}</b></div>`
          + (loV != null ? `<div style="opacity:.85;">${zoneLabelColon || tr('dashboard:labels.zoneWithColon')} <b>${fmt(loV)} – ${fmt(hiV)}</b></div>` : '')
          + deltaLine;
      },
    },
    xAxis: {
      type: 'category', data: days, boundaryGap: false,
      name: tr('common:time.day'), nameLocation: 'middle', nameGap: 26,
      nameTextStyle: { fontSize: 13, color: '#5b5f7d', fontWeight: 700 },
      axisLabel: { fontSize: 13, color: '#5b5f7d', fontWeight: 600, interval: Math.max(0, Math.floor(days.length / 7)) },
      axisLine: { lineStyle: { color: '#c9cde0' } },
      axisTick: { show: false },
    },
    yAxis: {
      type: 'value', minInterval: yInterval, scale: true,
      // Snap to the data: floor/ceil to the tick interval with no extra step,
      // so the corridor fills the panel with no dead band above or below.
      min: (v) => Math.max(0, Math.floor(v.min / yInterval) * yInterval),
      max: (v) => Math.ceil(v.max / yInterval) * yInterval,
      axisLabel: { fontSize: 13, color: '#5b5f7d', fontWeight: 600 },
      axisLine: { show: false },
      axisTick: { show: false },
      splitLine: { lineStyle: { color: '#e9ebf6' } },
    },
    series,
  };
  return (
    <ReactECharts
      option={option}
      style={{ height, width: '100%' }}
      notMerge
      lazyUpdate
      // Re-measure after mount: covers a modal that finishes its transition a
      // frame later than onEntered reports.
      // Two passes: one just after mount, one after any modal transition has
      // certainly settled. A canvas measured mid-animation renders squashed
      // inside its container and never recovers on its own.
      onChartReady={(inst) => {
        const kick = () => { try { inst.resize(); } catch (e) { /* detached */ } };
        setTimeout(kick, 60); setTimeout(kick, 400);
      }}
    />
  );
}


// ===== Sensor charts (Barn Monitor tiles + the per-sensor trend modal) =====
//
// Both render with `opts={{ renderer: 'svg' }}`. That is not a style choice:
// App.css:43 declares a global `canvas { height: 200px !important }` that
// crushes every ECharts canvas in the app, and the only escape in the codebase
// is `.forecastChartBig canvas`. Producing an SVG means there is no canvas node
// to crush, which is the same trick components/dashboard/echart.js uses to make
// the Today's Trends tab work. Do not drop `opts` from either component.
//
// Neither option object contains a FUNCTION value anywhere -- formatters are
// ECharts string templates and axis intervals are precomputed numbers.
// echarts-for-react diffs `option` with fast-deep-equal, so a value-only option
// makes the dashboard's 5-minute poll a true no-op when the data has not moved.

const BAND_HUE = '#22C55E';

// Mean-bucket a series down to at most `maxPoints`. Two things need this: a
// ~200px tile cannot show 300 points, and `getReadings` does not filter
// subDeviceID, so a barn with two probes returns interleaved A/B rows that
// render as a grey zigzag. Averaging kills both. The modal plots raw.
function downsample(arr, maxPoints) {
  if (arr.length <= maxPoints) return arr;
  const out = [];
  const size = arr.length / maxPoints;
  for (let i = 0; i < maxPoints; i++) {
    const s = Math.floor(i * size);
    const e = Math.min(arr.length, Math.floor((i + 1) * size));
    let sum = 0, c = 0;
    for (let j = s; j < e; j++) { sum += arr[j]; c++; }
    if (c) out.push(sum / c);
  }
  return out;
}

// Readings are rounded to 1dp server-side, so a still day is a flat line with
// ±0.1 of rounding noise. Without a floor on the span, `scale: true` magnifies
// that noise into a mountain range -- the classic sparkline lie. `minSpan`
// makes an in-range day read genuinely flat and an excursion genuinely spike.
function padDomain(min, max, minSpan) {
  let lo = min, hi = max;
  if (minSpan > 0 && hi - lo < minSpan) {
    const mid = (lo + hi) / 2;
    lo = mid - minSpan / 2;
    hi = mid + minSpan / 2;
  }
  const pad = (hi - lo) * 0.06 || 1;
  return [lo - pad, hi + pad];
}

// Tile sparkline: shape only. Deliberately scaled to the DATA, not to the
// target band -- at 32px the job is "is it moving", and the tile's border
// colour plus the line colour already carry "is it in range". The modal is
// where the band gets shown.
export function SensorSparkline({
  values, color = '#4f46e5', minSpan = 0, height = 32, maxPoints = 40,
}) {
  const vals = (values || []).filter((v) => Number.isFinite(v));
  if (vals.length < 2) return null;

  const pts = downsample(vals, maxPoints);
  const [yMin, yMax] = padDomain(Math.min(...pts), Math.max(...pts), minSpan);
  const last = pts.length - 1;

  const option = {
    animation: false,
    // right:3 leaves room for the latest-reading dot. markPoint escapes the
    // grid but NOT the root <svg>, so at right:0 it renders as a half-circle.
    grid: { left: 0, right: 3, top: 3, bottom: 0 },
    xAxis: {
      type: 'category', show: false, boundaryGap: false,
      data: pts.map((_, i) => i),
    },
    yAxis: { type: 'value', show: false, min: yMin, max: yMax },
    series: [{
      type: 'line', data: pts, smooth: true, symbol: 'none', silent: true,
      lineStyle: { color, width: 2 },
      areaStyle: {
        color: {
          type: 'linear', x: 0, y: 0, x2: 0, y2: 1,
          colorStops: [
            { offset: 0, color: hexToRgba(color, 0.30) },
            { offset: 1, color: hexToRgba(color, 0.02) },
          ],
        },
      },
      markPoint: {
        silent: true, symbol: 'circle', symbolSize: 6,
        itemStyle: { color, borderColor: '#fff', borderWidth: 1.5 },
        label: { show: false },
        data: [{ coord: [last, pts[last]] }],
      },
    }],
  };

  return (
    <ReactECharts
      option={option}
      style={{ height, width: '100%' }}
      opts={{ renderer: 'svg' }}
      notMerge
      lazyUpdate
    />
  );
}

// Full-size trend for the modal: raw readings, with the target band shaded.
export function SensorTrendChart({
  xLabels, values, lo = null, hi = null, color = '#4f46e5',
  unit = '', decimals = 1, minSpan = 0, height = 400,
}) {
  const { t: tr } = useTranslation(['dashboard', 'common', 'charts']);
  const vals = (values || []).map((v) => (Number.isFinite(Number(v)) ? Number(v) : null));
  const finite = vals.filter((v) => v !== null);
  if (!finite.length) return null;

  // The band is drawn with markArea, and markArea does NOT participate in axis
  // extent -- verified in echarts 5.5: nothing under coord/ or component/axis/
  // references it. With `scale: true` alone, a day running 33-36 against a
  // 22-32 target would put the band entirely off-screen, exactly when it
  // matters most. So the domain is the union of the data and the target.
  let yLo = Math.min(...finite);
  let yHi = Math.max(...finite);
  if (lo !== null) { yLo = Math.min(yLo, lo); yHi = Math.max(yHi, lo); }
  if (hi !== null) { yLo = Math.min(yLo, hi); yHi = Math.max(yHi, hi); }
  const allNonNeg = Math.min(...finite) >= 0
    && (lo === null || lo >= 0) && (hi === null || hi >= 0);
  let [yMin, yMax] = padDomain(yLo, yHi, minSpan);
  if (allNonNeg) yMin = Math.max(0, yMin);

  // A null bound is an OPEN band edge, not a bound at zero: ECharts resolves a
  // missing yAxis on a markArea corner to ±Infinity and snaps it to the plot
  // edge. That is exactly right for CO2/NH3, which have a ceiling and no floor.
  // Both bounds absent would wash the entire plot in the band colour, so the
  // markArea is dropped altogether in that case.
  const hasBand = lo !== null || hi !== null;
  const markLineData = [];
  if (lo !== null) markLineData.push({ yAxis: lo, name: tr('common:metrics.min') });
  if (hi !== null) markLineData.push({ yAxis: hi, name: tr('common:metrics.max') });

  const n = vals.length;
  const seriesName = tr('charts:axis.reading', { defaultValue: 'Reading' });

  const option = {
    animation: false,
    grid: { left: 52, right: 24, top: 16, bottom: 40 },
    // A string template, not a function -- see the note at the top of this
    // section. `confine` stops the tooltip escaping the modal at the right edge.
    tooltip: {
      trigger: 'axis', confine: true,
      formatter: `{b}<br/>{a}: {c}${unit}`,
    },
    xAxis: {
      type: 'category', boundaryGap: false, data: xLabels || [],
      // ~8 labels instead of 300 overlapping ones.
      axisLabel: { interval: Math.max(0, Math.ceil(n / 8) - 1), fontSize: 11, color: '#6b6bb8' },
      axisLine: { lineStyle: { color: '#dfe2f4' } },
      splitLine: { show: false },
    },
    yAxis: {
      type: 'value', min: yMin, max: yMax,
      // The domain ends are padding, not meaningful values, and printing them
      // exposes float noise (76.8 + 6% pad renders as "76.24799999999999" and
      // overflows the axis gutter). Let ECharts place its own round ticks
      // inside the domain instead.
      axisLabel: {
        fontSize: 11, color: '#6b6bb8',
        showMinLabel: false, showMaxLabel: false,
      },
      splitLine: { lineStyle: { color: '#eef0fb' } },
    },
    series: [{
      name: seriesName,
      type: 'line', data: vals, smooth: true, symbol: 'none',
      connectNulls: false,
      lineStyle: { color, width: 2.4 },
      itemStyle: { color },
      areaStyle: {
        color: {
          type: 'linear', x: 0, y: 0, x2: 0, y2: 1,
          colorStops: [
            { offset: 0, color: hexToRgba(color, 0.22) },
            { offset: 1, color: hexToRgba(color, 0.01) },
          ],
        },
      },
      markArea: hasBand ? {
        silent: true,
        itemStyle: { color: hexToRgba(BAND_HUE, 0.10) },
        data: [[{ yAxis: lo }, { yAxis: hi }]],
      } : undefined,
      markLine: markLineData.length ? {
        silent: true, symbol: 'none',
        lineStyle: { color: '#9aa0c0', type: 'dashed', width: 1 },
        label: { formatter: '{b}', position: 'insideEndTop', fontSize: 10, color: '#6b6bb8' },
        data: markLineData,
      } : undefined,
    }],
  };

  return (
    <ReactECharts
      option={option}
      style={{ height, width: '100%' }}
      opts={{ renderer: 'svg' }}
      notMerge
      lazyUpdate
      // Same double-resize kick as ProjectionMiniChart: echarts-for-react
      // swallows the first size-sensor callback, so a chart initialised at the
      // wrong width inside an animating dialog never self-heals.
      onChartReady={(inst) => {
        const kick = () => { try { inst.resize(); } catch (e) { /* detached */ } };
        setTimeout(kick, 60); setTimeout(kick, 400);
      }}
    />
  );
}
